import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { sendAPIRequestEmail } from '../utils/emailService';

const APIAccessForm = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    position: '',
    message: '',
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [lastSubmissionTime, setLastSubmissionTime] = useState(0);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const sanitizeInput = (input) => {
    // Basic XSS prevention - remove potentially dangerous characters
    return input.replace(/[<>]/g, '').trim();
  };

  const validateForm = () => {
    const newErrors = {};

    // Sanitize and validate first name
    const firstName = sanitizeInput(formData.firstName);
    if (!firstName) {
      newErrors.firstName = 'First name is required';
    } else if (firstName.length < 2 || firstName.length > 50) {
      newErrors.firstName = 'First name must be between 2 and 50 characters';
    }

    // Sanitize and validate last name
    const lastName = sanitizeInput(formData.lastName);
    if (!lastName) {
      newErrors.lastName = 'Last name is required';
    } else if (lastName.length < 2 || lastName.length > 50) {
      newErrors.lastName = 'Last name must be between 2 and 50 characters';
    }

    // Validate email
    const email = sanitizeInput(formData.email);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    } else if (email.length > 100) {
      newErrors.email = 'Email must be less than 100 characters';
    }

    // Sanitize and validate company
    const company = sanitizeInput(formData.company);
    if (!company) {
      newErrors.company = 'Company name is required';
    } else if (company.length < 2 || company.length > 100) {
      newErrors.company = 'Company name must be between 2 and 100 characters';
    }

    // Sanitize and validate position
    const position = sanitizeInput(formData.position);
    if (!position) {
      newErrors.position = 'Position is required';
    } else if (position.length < 2 || position.length > 100) {
      newErrors.position = 'Position must be between 2 and 100 characters';
    }

    // Sanitize and validate message
    const message = sanitizeInput(formData.message);
    if (!message) {
      newErrors.message = 'Message is required';
    } else if (message.length < 10 || message.length > 1000) {
      newErrors.message = 'Message must be between 10 and 1000 characters';
    }

    return newErrors;
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    // Rate limiting check
    const currentTime = Date.now();
    if (currentTime - lastSubmissionTime < 30000) {
      setErrors({ submit: 'Please wait 30 seconds before submitting again.' });
      return;
    }

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      // Send email using EmailJS
      await sendAPIRequestEmail(formData);
      
      setLastSubmissionTime(currentTime);
      setIsSubmitted(true);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        company: '',
        position: '',
        message: '',
      });

      // Scroll to the success message section
      setTimeout(() => {
        const successSection = document.getElementById('api-access');
        if (successSection) {
          successSection.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    } catch (error) {
      console.error('Email sending failed:', error);
      // Handle different types of errors
      if (error.message.includes('not properly configured')) {
        setErrors({ submit: 'Email service is not configured. Please contact support.' });
      } else if (error.status === 400) {
        setErrors({ submit: 'Invalid email configuration. Please try again later.' });
      } else if (error.status === 429) {
        setErrors({ submit: 'Too many requests. Please wait a moment and try again.' });
      } else {
        setErrors({ submit: 'Failed to send email. Please try again or contact support.' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <section id="api-access" className="section-padding-mobile bg-gradient-to-br from-primary-50 via-white to-accent-50 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 -right-12 w-48 h-48 xs:-right-24 xs:w-96 xs:h-96 bg-green-100/50 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 -left-12 w-48 h-48 xs:-left-24 xs:w-96 xs:h-96 bg-primary-100/50 rounded-full blur-3xl"></div>
        </div>

        <div className="container-mobile relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto text-center"
          >
            <div className="bg-white rounded-2xl xs:rounded-3xl shadow-mobile-large xs:shadow-large p-8 xs:p-12 lg:p-16 border border-gray-100 relative overflow-hidden">
              {/* Success icon with animation */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="relative w-20 h-20 xs:w-24 xs:h-24 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 xs:mb-8 shadow-lg"
              >
                <svg className="w-10 h-10 xs:w-12 xs:h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <motion.path
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="3"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </motion.div>

              <h2 className="text-2xl xs:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 xs:mb-6">
                Thank You!
              </h2>
              <p className="text-lg xs:text-xl text-gray-600 mb-6 xs:mb-8 leading-relaxed">
                Your API access request has been submitted successfully. Our team will review your request and get back to you within <span className="font-semibold text-primary-600">24 hours</span>.
              </p>
              
              {/* Additional info */}
              <div className="bg-primary-50 rounded-xl xs:rounded-2xl p-4 xs:p-6 mb-6 xs:mb-8 border border-primary-100">
                <h3 className="font-semibold text-primary-900 mb-3">What's Next?</h3>
                <ul className="text-sm text-primary-700 space-y-2 text-left max-w-md mx-auto">
                  <li className="flex items-center">
                    <span className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-3"></span>
                    Our team will review your application
                  </li>
                  <li className="flex items-center">
                    <span className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-3"></span>
                    You'll receive API credentials via email
                  </li>
                  <li className="flex items-center">
                    <span className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-3"></span>
                    Access to comprehensive documentation
                  </li>
                </ul>
              </div>

              <div className="flex justify-center">
                <button
                  onClick={() => setIsSubmitted(false)}
                  className="btn-secondary-mobile group min-h-touch-md"
                >
                  Submit Another Request
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="api-access" className="section-padding-mobile bg-gradient-to-br from-gray-25 via-white to-primary-25 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 -left-24 w-48 h-48 xs:-left-48 xs:w-96 xs:h-96 bg-primary-100/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 -right-24 w-48 h-48 xs:-right-48 xs:w-96 xs:h-96 bg-accent-100/30 rounded-full blur-3xl"></div>
      </div>

      <div className="container-mobile relative">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12 xs:mb-16 lg:mb-20"
        >

          <h2 className="text-mobile-title font-bold text-gray-900 mb-4 xs:mb-6">
            Request{' '}
            <span className="gradient-text">API Access</span>
          </h2>
          
          <p className="text-mobile-body text-gray-600 max-w-4xl mx-auto mb-6 xs:mb-8">
            Get started with CIVIONIKS API and transform your infrastructure monitoring capabilities
          </p>

          {/* Trust indicators */}
          <div className="flex flex-col gap-3 xs:flex-row xs:gap-8 justify-center items-center text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>Enterprise-grade security</span>
            </div>
            <div className="hidden xs:block w-1 h-1 bg-gray-300 rounded-full"></div>
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21.192 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m2.829 2.829L9.88 19.88M12 13a1 1 0 100-2 1 1 0 000 2z" />
              </svg>
              <span>24/7 support included</span>
            </div>
            <div className="hidden xs:block w-1 h-1 bg-gray-300 rounded-full"></div>
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-purple-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
              </svg>
              <span>Fast setup in minutes</span>
            </div>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 xs:gap-12 lg:gap-16 items-start">
          {/* Left side - Benefits */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:pt-8"
          >
            <h3 className="text-xl xs:text-2xl lg:text-3xl font-semibold text-gray-900 mb-6 xs:mb-8">
              Why choose CIVIONIKS API?
            </h3>

            <div className="space-y-4 xs:space-y-6">
              {[
                {
                  icon: '🚀',
                  title: 'Rapid Integration',
                  description: 'RESTful API with comprehensive SDKs for major programming languages'
                },
                {
                  icon: '📊',
                  title: 'Real-time Data',
                  description: 'Stream live sensor data with WebSocket connections and 99.9% uptime'
                },
                {
                  icon: '🔒',
                  title: 'Enterprise Security',
                  description: 'OAuth 2.0, rate limiting, and SOC 2 Type II compliance included'
                },
                {
                  icon: '📈',
                  title: 'Scalable Architecture',
                  description: 'Handle millions of data points with auto-scaling infrastructure'
                }
              ].map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                  transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                  className="flex items-start gap-3 xs:gap-4 p-3 xs:p-4 rounded-xl xs:rounded-2xl hover:bg-white hover:shadow-mobile-soft xs:hover:shadow-soft transition-all duration-300"
                >
                  <div className="text-xl xs:text-2xl bg-white rounded-lg xs:rounded-xl p-2 shadow-mobile-soft xs:shadow-soft">
                    {benefit.icon}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1 xs:mb-2 text-sm xs:text-base">{benefit.title}</h4>
                    <p className="text-gray-600 leading-relaxed text-sm xs:text-base">{benefit.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Social proof */}
            <div className="mt-8 xs:mt-12 p-4 xs:p-6 bg-white rounded-xl xs:rounded-2xl shadow-mobile-soft xs:shadow-soft border border-gray-100">
              <div className="text-center">
                <p className="text-sm text-gray-500 mb-4">Trusted by leading engineering teams</p>
                <div className="flex flex-col gap-2 xs:flex-row xs:gap-6 items-center justify-center opacity-60">
                  <div className="text-xs font-medium">Fortune 500</div>
                  <div className="hidden xs:block w-1 h-1 bg-gray-300 rounded-full"></div>
                  <div className="text-xs font-medium">Government Agencies</div>
                  <div className="hidden xs:block w-1 h-1 bg-gray-300 rounded-full"></div>
                  <div className="text-xs font-medium">Research Institutions</div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right side - Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: 30 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <div className="bg-white rounded-2xl xs:rounded-3xl shadow-mobile-large xs:shadow-large p-6 xs:p-8 lg:p-10 border border-gray-100 relative">
              {/* Form header */}
              <div className="text-center mb-6 xs:mb-8">
                <h3 id="api-access-heading" className="text-xl xs:text-2xl font-semibold text-gray-900 mb-2 xs:mb-3">Get Started Today</h3>
                <p className="text-gray-600 text-sm xs:text-base">Fill out the form below and we'll get you set up within 24 hours</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5 xs:space-y-6">
                {/* Name fields */}
                <div className="grid grid-cols-1 xs:grid-cols-2 gap-4 xs:gap-4">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-semibold text-gray-700 mb-2 xs:mb-3">
                      First Name *
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      className={`form-input-mobile ${
                        errors.firstName ? 'border-red-500' : 'border-gray-200'
                      }`}
                      placeholder="John"
                    />
                    {errors.firstName && (
                      <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {errors.firstName}
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="lastName" className="block text-sm font-semibold text-gray-700 mb-2 xs:mb-3">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      className={`form-input-mobile ${
                        errors.lastName ? 'border-red-500' : 'border-gray-200'
                      }`}
                      placeholder="Doe"
                    />
                    {errors.lastName && (
                      <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {errors.lastName}
                      </p>
                    )}
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2 xs:mb-3">
                    Work Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`form-input-mobile ${
                      errors.email ? 'border-red-500' : 'border-gray-200'
                    }`}
                    placeholder="john@company.com"
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.email}
                    </p>
                  )}
                </div>

                {/* Company and Position */}
                <div className="grid grid-cols-1 xs:grid-cols-2 gap-4 xs:gap-4">
                  <div>
                    <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2 xs:mb-3">
                      Company *
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className={`form-input-mobile ${
                        errors.company ? 'border-red-500' : 'border-gray-200'
                      }`}
                      placeholder="Acme Corp"
                    />
                    {errors.company && (
                      <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {errors.company}
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="position" className="block text-sm font-semibold text-gray-700 mb-2 xs:mb-3">
                      Position *
                    </label>
                    <input
                      type="text"
                      id="position"
                      name="position"
                      value={formData.position}
                      onChange={handleChange}
                      className={`form-input-mobile ${
                        errors.position ? 'border-red-500' : 'border-gray-200'
                      }`}
                      placeholder="Software Engineer"
                    />
                    {errors.position && (
                      <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {errors.position}
                      </p>
                    )}
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2 xs:mb-3">
                    Tell us about your project *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows="4"
                    value={formData.message}
                    onChange={handleChange}
                    className={`form-textarea-mobile ${
                      errors.message ? 'border-red-500' : 'border-gray-200'
                    }`}
                    placeholder="Tell us about your infrastructure monitoring needs, expected data volume, and integration requirements..."
                  />
                  {errors.message && (
                    <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.message}
                    </p>
                  )}
                </div>

                {/* Submit Error */}
                {errors.submit && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      {errors.submit}
                    </div>
                  </div>
                )}

                {/* Submit button */}
                <div className="pt-2 xs:pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full py-4 px-6 xs:px-8 rounded-xl font-semibold text-base xs:text-lg transition-all duration-300 group min-h-touch-md ${
                      isSubmitting
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'btn-primary shadow-primary hover:shadow-large'
                    }`}
                    aria-describedby={errors.submit ? 'submit-error' : undefined}
                  >
                    {isSubmitting ? (
                      <div className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="sr-only">Submitting form...</span>
                        Submitting...
                      </div>
                    ) : (
                      <>
                        <span>Request API Access</span>
                        <svg className="w-4 h-4 xs:w-5 xs:h-5 ml-2 group-hover:translate-x-0.5 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                      </>
                    )}
                  </button>
                </div>

                {/* Privacy note */}
                <p className="text-xs text-gray-500 text-center leading-relaxed">
                  By submitting this form, you agree to our{' '}
                  <button className="text-primary-600 hover:text-primary-700 underline focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded">Terms of Service</button>
                  {' '}and{' '}
                  <button className="text-primary-600 hover:text-primary-700 underline focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded">Privacy Policy</button>.
                  We'll never share your information.
                </p>
              </form>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Section divider */}
      <div className="section-divider mt-16 xs:mt-20 lg:mt-24"></div>
    </section>
  );
};

export default APIAccessForm; 